import {
  ControlRequirementLinksService,
  ControlRiskLinksService,
  ControlVendorLinksService,
} from "../api/generated";
import apiClient from "../api/apiClient";
import { withApi } from "../api/openapiClient";

const listPoliciesForControl = async (controlId) => {
  return withApi(
    (async () => {
      const response = await apiClient.get(`/controls/${Number(controlId)}/tenant-policies`);
      return response?.data ?? [];
    })()
  );
};

const linkControlToTenantPolicy = async (controlId, tenantPolicyId, payload = {}) => {
  return withApi(
    (async () => {
      const response = await apiClient.post(
        `/controls/${Number(controlId)}/tenant-policies/${Number(tenantPolicyId)}`,
        null,
        {
          params: {
            created_by: payload?.created_by ?? payload?.createdBy ?? undefined,
          },
        }
      );
      return response?.data ?? null;
    })()
  );
};

const unlinkControlFromTenantPolicy = async (controlId, tenantPolicyId) => {
  return withApi(
    (async () => {
      const response = await apiClient.delete(
        `/controls/${Number(controlId)}/tenant-policies/${Number(tenantPolicyId)}`
      );
      return response?.data ?? null;
    })()
  );
};

const listFetchers = {
  risks: (controlId) =>
    withApi(
      ControlRiskLinksService.listRisksForControlControlsControlIdRisksGet({
        controlId: Number(controlId),
      })
    ),
  vendors: (controlId) =>
    withApi(
      ControlVendorLinksService.listVendorsForControlControlsControlIdVendorsGet({
        controlId: Number(controlId),
      })
    ),
  policies: (controlId) => listPoliciesForControl(controlId),
  requirements: (controlId) =>
    withApi(
      ControlRequirementLinksService.listRequirementsForControlControlsControlIdRequirementsGet({
        controlId: Number(controlId),
      })
    ),
};

const linkers = {
  risks: (controlId, entityId, payload = {}) =>
    withApi(
      ControlRiskLinksService.linkControlToRiskControlsControlIdRisksRiskIdPost({
        controlId: Number(controlId),
        riskId: Number(entityId),
        createdBy: payload?.created_by ?? payload?.createdBy ?? undefined,
      })
    ),
  vendors: (controlId, entityId, payload = {}) =>
    withApi(
      ControlVendorLinksService.linkControlToVendorControlsControlIdVendorsVendorIdPost({
        controlId: Number(controlId),
        vendorId: Number(entityId),
        createdBy: payload?.created_by ?? payload?.createdBy ?? undefined,
      })
    ),
  policies: (controlId, entityId, payload = {}) =>
    linkControlToTenantPolicy(controlId, entityId, payload),
  requirements: (controlId, entityId, payload = {}) =>
    withApi(
      ControlRequirementLinksService.linkControlToRequirementControlsControlIdRequirementsRequirementIdPost({
        controlId: Number(controlId),
        requirementId: Number(entityId),
        createdBy: payload?.created_by ?? payload?.createdBy ?? undefined,
      })
    ),
};

const unlinkers = {
  risks: (controlId, entityId) =>
    withApi(
      ControlRiskLinksService.unlinkControlFromRiskControlsControlIdRisksRiskIdDelete({
        controlId: Number(controlId),
        riskId: Number(entityId),
      })
    ),
  vendors: (controlId, entityId) =>
    withApi(
      ControlVendorLinksService.unlinkControlFromVendorControlsControlIdVendorsVendorIdDelete({
        controlId: Number(controlId),
        vendorId: Number(entityId),
      })
    ),
  policies: (controlId, entityId) => unlinkControlFromTenantPolicy(controlId, entityId),
  requirements: (controlId, entityId) =>
    withApi(
      ControlRequirementLinksService.unlinkControlFromRequirementControlsControlIdRequirementsRequirementIdDelete({
        controlId: Number(controlId),
        requirementId: Number(entityId),
      })
    ),
};

export const emptyControlLinks = {
  risks: [],
  vendors: [],
  policies: [],
  requirements: [],
};

export async function fetchControlLinks(controlId) {
  const entries = Object.entries(listFetchers);
  const results = await Promise.all(
    entries.map(async ([key, fn]) => {
      try {
        const res = await fn(controlId);
        return { key, data: res.data || [] };
      } catch (error) {
        return { key, error };
      }
    })
  );

  const links = { ...emptyControlLinks };
  const errors = {};

  results.forEach(({ key, data, error }) => {
    if (error) {
      errors[key] = error;
      links[key] = [];
    } else {
      links[key] = Array.isArray(data) ? data : [];
    }
  });

  return { links, errors };
}

export async function linkControlEntity(controlId, entityType, entityId, payload) {
  const linker = linkers[entityType];
  if (!linker) {
    throw new Error(`Unsupported link entity type: ${entityType}`);
  }
  return linker(controlId, entityId, payload);
}

export async function unlinkControlEntity(controlId, entityType, entityId) {
  const unlinker = unlinkers[entityType];
  if (!unlinker) {
    throw new Error(`Unsupported link entity type: ${entityType}`);
  }
  return unlinker(controlId, entityId);
}
